import React, { useState } from "react";
import "./css/Loginpage.css";
import { FaEnvelope, FaLock } from "react-icons/fa";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Header from "../components/LandingComponents/Header";
import LandingFooter from "../components/LandingComponents/LandingFooter";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useLoginMutation } from "../slices/userApiSlice"; // Import login mutation
import { setCredentials } from "../slices/authSlice";
import { toast } from "sonner";

const Loginpage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation(); // Login mutation
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const validateForm = () => {
    let newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm() && !isLoading) {
      const loginPromise = new Promise(async (resolve, reject) => {
        try {
          const response = await login({ email, password }).unwrap();

          // Extract role from response using Set for better performance
          const authorities = response.user.authorities;
          const validRoles = new Set([
            "Admin",
            "Manager",
            "Super Admin",
            "Supervisor",
            "Technician",
            "CFM",
          ]);
          const userRole =
            authorities.find((auth) => validRoles.has(auth.authority))
              ?.authority || "Unknown";

          // Dispatch once: user, userRole, and token all together
          dispatch(
            setCredentials({
              user: response.user,
              userRole,
              token: response.token,
            }),
          );

          // Set session start time
          sessionStorage.setItem("sessionStartTime", Date.now().toString());

          // Role-based navigation
          if (userRole === "Admin") navigate("/admin");
          else if (userRole === "Manager") navigate("/manager");
          else if (userRole === "Super Admin") navigate("/superadmin");
          else if (userRole === "Supervisor") navigate("/supervisor");
          else if (userRole === "Technician") navigate("/technician");
          else if (userRole === "CFM") navigate("/cfm");
          else navigate("/login");

          resolve("Login Successful");
        } catch (err) {
          console.error("Login Error:", err);
          const errorMessage =
            err?.data?.message ||
            "Invalid email or password. Please try again.";
          reject(errorMessage);
        }
      });

      toast.promise(loginPromise, {
        loading: "Logging in...",
        success: (data) => `${data}`,
        error: (err) => `${err}`,
      });
    }
  };

  return (
    <div className="login-wrapper">
      <Header />
      <div className="login-main">
        {/* Background Image Container */}
        <div className="login-bg-overlay"></div>

        <div className="login-card">
          <div className="login-card-header">
            <h2 className="login-title">Staff Portal</h2>
            <p className="login-subtitle">
              Access your account to manage facilities and services.
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label">Email Address</label>
              <div
                className={`login-input-wrapper ${errors.email ? "error" : ""}`}
              >
                <FaEnvelope className="login-field-icon" />
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="login-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {errors.email && (
                <span className="login-error-text">{errors.email}</span>
              )}
            </div>

            <div className="login-field">
              <div className="login-label-row">
                <label className="login-label">Password</label>
                <Link to="/forgotpassword" id="forgot-password-link">
                  Forgot Password?
                </Link>
              </div>
              <div
                className={`login-input-wrapper ${errors.password ? "error" : ""
                  }`}
              >
                <FaLock className="login-field-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  className="login-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="login-toggle-btn"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <AiFillEye /> : <AiFillEyeInvisible />}
                </button>
              </div>
              {errors.password && (
                <span className="login-error-text">{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className="login-btn-primary"
              disabled={isLoading}
            >
              {isLoading ? <div className="login-spinner"></div> : "Sign In"}
            </button>
          </form>

          <div className="login-card-footer">
            <p>
              Need technical assistance?{" "}
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById("contact-support-section")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="contact-support-link"
              >
                Contact Support
              </button>
            </p>
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
};

export default Loginpage;
